import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Copy, Check, Loader2, MessageSquare } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Send to a loved one" — lets a family member (the booking patient) generate a no-login
 * Visit Pass link for the senior receiving care, optionally texting it to them. Embodies
 * the family-books-on-behalf model: the senior just taps the link to attend solo.
 */
export const ShareVisitButton = ({ appointmentId, testId = 'share-visit-btn' }) => {
  const { getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [passUrl, setPassUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const createLink = async (sendSms) => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/appointments/${appointmentId}/share`,
        { recipient_name: name || undefined, recipient_phone: phone || undefined, send_sms: !!sendSms },
        getAuthHeader(),
      );
      setPassUrl(data.pass_url);
      if (sendSms && data.sms_attempted) toast.success(`Text message sent to ${phone}`);
      else toast.success('Visit link ready to share');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create the link');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(passUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error('Copy failed — please select and copy the link');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPassUrl(''); setCopied(false); } }}>
      <DialogTrigger asChild>
        <button
          data-testid={testId}
          className="text-xs font-medium text-cn-primary hover:underline inline-flex items-center gap-1 px-2 py-1.5"
        >
          <Send className="w-3.5 h-3.5" /> Send to loved one
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="share-visit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Send this visit to your loved one</DialogTitle>
          <DialogDescription>
            They'll get a simple page — no login or app needed — to see the visit details and join with one tap.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="recipient-name">Their name (optional)</Label>
            <Input id="recipient-name" data-testid="share-name-input" placeholder="e.g. Mom, or Rose"
                   value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="recipient-phone">Their mobile number (optional — to text the link)</Label>
            <Input id="recipient-phone" data-testid="share-phone-input" placeholder="+1 555 123 4567"
                   value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" />
          </div>

          {passUrl ? (
            <div className="p-3 rounded-xl bg-cn-sage-100/60 border border-cn-border" data-testid="share-result">
              <p className="text-xs text-slate-500 mb-1.5">Shareable link</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs break-all text-slate-700">{passUrl}</code>
                <Button size="sm" variant="outline" onClick={copy} data-testid="share-copy-btn">
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          ) : null}

          <div className="flex gap-2 pt-1">
            <Button onClick={() => createLink(false)} disabled={loading} variant="outline"
                    className="flex-1" data-testid="share-create-link-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4 mr-1.5" />} Create link
            </Button>
            <Button onClick={() => createLink(true)} disabled={loading || !phone}
                    className="flex-1 cn-btn cn-btn-primary" data-testid="share-text-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-1.5" />} Text it to them
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareVisitButton;
